import { getCurrencyForLocale } from './CurrencyUtils';

type SupportedLocale = 'en-US' | 'fr-CA';

const mapLanguageToLocale = (lng: string): SupportedLocale => {
  if (lng?.startsWith('fr')) {
    return 'fr-CA';
  }
  return 'en-US';
};

const numberFormatterCache = new Map<string, Intl.NumberFormat>();

const getCachedNumberFormatter = (
  locale: SupportedLocale,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): Intl.NumberFormat => {
  const key = `${locale}:${minimumFractionDigits}:${maximumFractionDigits}`;
  let formatter = numberFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits });
    numberFormatterCache.set(key, formatter);
  }
  return formatter;
};

export const formatCurrency = (
  value: number,
  i18n: { language: string },
  maximumFractionDigits = 2,
) => {
  const locale = mapLanguageToLocale(i18n.language);
  const currency = getCurrencyForLocale(locale);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    maximumFractionDigits,
  }).format(value);
};

export const formatDate = (value: Date | string, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (locale === 'fr-CA') {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(isDateOnly ? { timeZone: 'UTC' } : {}),
    }).format(new Date(value));
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    ...(isDateOnly ? { timeZone: 'UTC' } : {}),
  }).format(new Date(value));
};

export const formatDateTime = (value: Date | string, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

export const formatFileSize = (bytes: number) => {
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

export const formatLongDate = (value: Date | string, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(isDateOnly ? { timeZone: 'UTC' } : {}),
  }).format(new Date(value));
};

export const formatNumber = (
  points: number,
  i18n: { language: string },
  options?: { maximumFractionDigits?: number },
) => {
  const locale = mapLanguageToLocale(i18n.language);
  const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
  const minimumFractionDigits = points % 1 === 0 ? 0 : 2;
  return getCachedNumberFormatter(
    locale,
    minimumFractionDigits,
    maximumFractionDigits,
  ).format(points);
};

/**
 * Formats a percentage value. The input value is expected to be in the range of 0 to 100 (e.g., 25 for 25%).
 * @param value
 * @param i18n
 * @param options
 */
export const formatPercent = (
  value: number,
  i18n: { language: string },
  options?: { maximumFractionDigits?: number, minimumFractionDigits?: number },
) => {
  const locale = mapLanguageToLocale(i18n.language);
  const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value / 100);
};

export const formatShortCurrency = (value: number, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  const currency = getCurrencyForLocale(locale);

  const formatWithSuffix = (val: number, suffix: string) => {
    const numberStr = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(val);
    // fr-CA places the currency symbol after the number (e.g. "1,5k $").
    // en-US places it before (e.g. "$1.5k").
    // Avoid formatToParts — it is not supported on all iOS JavaScriptCore versions.
    if (locale === 'fr-CA') {
      const unicodeNBSP = '\u00A0';
      return `${numberStr}${suffix}${unicodeNBSP}$`;
    }
    return `$${numberStr}${suffix}`;
  };

  if (value >= 1000 * 1000) {
    return formatWithSuffix(value / 1000 / 1000, 'M');
  } else if (value >= 1000) {
    return formatWithSuffix(value / 1000, 'k');
  } else {
    const isWholeDollar = value % 1 === 0;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: isWholeDollar ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
};

export const formatShortNumber = (amount: number, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  let result: string = '';
  if (amount >= 1000 * 1000) {
    const formattedAmount = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(amount / 1000 / 1000);
    result = `${formattedAmount}M`;
  } else if (amount >= 1000) {
    const formattedAmount = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(amount / 1000);
    result = `${formattedAmount}k`;
  } else {
    result = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).format(amount);
  }
  return result;
};

export const formatMediumDate = (value: Date | string, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(isDateOnly ? { timeZone: 'UTC' } : {}),
  }).format(new Date(value));
};

export const formatTime = (value: Date | string, i18n: { language: string }) => {
  const locale = mapLanguageToLocale(i18n.language);
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};
